/**
 * AgentDocumentsHub — condensed OPS-style document list for the lead workspace.
 * Scoped to the active project: shows files tagged to it plus untagged
 * lead-level files. One tight row per file; agent synopsis expands on demand.
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { query, orderBy, onSnapshot } from 'firebase/firestore';
import { leadCommentsCollectionRef } from '../../utils/leadCommentsFirestore';
import { db } from '../../utils/firebase';
import { useAuth } from '../../utils/AuthContext';
import {
  getLeadDocuments,
  deleteLeadDocument,
  updateLeadDocumentDealId,
  isImageType,
} from '../../utils/leadDocumentService';
import {
  leadDocumentUploadManager,
  type LeadUploadBatch,
  getBatchProgressPct,
  formatBatchEta,
  formatBatchSummary,
  formatUploadBytes,
} from '../../utils/leadDocumentUploadManager';
import { isCsvType } from '../../utils/projectFileService';
import { subscribeToAnnotationsForLead } from '../../utils/pdfAnnotationService';
import { subscribeToTakeoffLinksForLead } from '../../utils/pdfTakeoffLinkService';
import { firestoreSnapshotListenError } from '../../utils/firestoreListenDebug';
import type { LeadDocument, CommentAttachment, PdfAnnotation, Lead } from '../../types';
import {
  FileText,
  Image as ImageIcon,
  Table2,
  File,
  Plus,
  Download,
  Trash2,
  Loader2,
  FolderOpen,
  Sparkles,
  MessageSquare,
  MapPin,
  ChevronDown,
} from 'lucide-react';

export interface DealFilterOption {
  id: string;
  name: string;
}

type DisplayDoc = {
  id: string;
  name: string;
  url: string;
  size: number;
  mimeType: string;
  dealId?: string;
  source: 'upload' | 'comment';
  uploadedAt?: Date;
} & (LeadDocument | { storagePath?: never });

interface AgentDocumentsHubProps {
  leadId: string;
  lead: Lead;
  deals?: DealFilterOption[];
  activeDealId?: string | null;
  /** Reports the total document count (uploads + comment files) to the parent. */
  onDocCountChange?: (count: number) => void;
  /** Shared with Team Notes strip — one comments listener for the whole workspace. */
  onCommentCountChange?: (count: number) => void;
  onLeadPinCountChange?: (count: number) => void;
  onOpenPdf: (doc: { url: string; name: string; id?: string }, opts?: { annotationId?: string; pageNumber?: number }) => void;
  onOpenImage?: (doc: { url: string; name: string; id?: string; mimeType?: string }, opts?: { annotationId?: string }) => void;
  onOpenCsv?: (doc: { url: string; name: string; id?: string; mimeType?: string }, opts?: { annotationId?: string }) => void;
}

function docTypeLabel(mimeType: string, name: string): 'PDF' | 'Image' | 'CSV' | 'File' {
  if (isImageType(mimeType)) return 'Image';
  if (isCsvType(mimeType, name)) return 'CSV';
  if (mimeType === 'application/pdf' || /\.pdf$/i.test(name)) return 'PDF';
  return 'File';
}

function typeIcon(label: 'PDF' | 'Image' | 'CSV' | 'File') {
  if (label === 'Image') return <ImageIcon size={13} />;
  if (label === 'CSV') return <Table2 size={13} />;
  if (label === 'PDF') return <FileText size={13} />;
  return <File size={13} />;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Merge annotations keyed by document id or url (deduped by annotation id). */
function annotationsForDocument(
  byKey: Map<string, PdfAnnotation[]>,
  documentId: string,
  documentUrl: string,
): PdfAnnotation[] {
  const seen = new Set<string>();
  const merged: PdfAnnotation[] = [];
  for (const key of [documentId, documentUrl]) {
    if (!key) continue;
    for (const ann of byKey.get(key) ?? []) {
      if (ann.id) {
        if (seen.has(ann.id)) continue;
        seen.add(ann.id);
      }
      merged.push(ann);
    }
  }
  return merged.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
}

export const AgentDocumentsHub: React.FC<AgentDocumentsHubProps> = ({
  leadId,
  lead,
  deals,
  activeDealId,
  onDocCountChange,
  onCommentCountChange,
  onLeadPinCountChange,
  onOpenPdf,
  onOpenImage,
  onOpenCsv,
}) => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState<LeadDocument[]>([]);
  const [commentFiles, setCommentFiles] = useState<Array<{ att: CommentAttachment; createdAt: Date }>>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadBatch, setUploadBatch] = useState<LeadUploadBatch | undefined>();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [annotationsByKey, setAnnotationsByKey] = useState<Map<string, PdfAnnotation[]>>(new Map());
  const [takeoffCountByDocId, setTakeoffCountByDocId] = useState<Map<string, number>>(new Map());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const trackedBatchRef = useRef<string | null>(null);
  const mergedUploadFileIdsRef = useRef<Set<string>>(new Set());
  const reconciledBatchIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getLeadDocuments(leadId)
      .then(docs => { if (!cancelled) setDocuments(docs); })
      .catch(err => { if (!cancelled) console.error('Error loading documents:', err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [leadId]);

  useEffect(() => {
    return leadDocumentUploadManager.subscribe(() => {
      const batch = leadDocumentUploadManager.getBatchForLead(leadId);
      if (!batch) {
        setUploadBatch(undefined);
        setUploading(false);
        return;
      }
      if (batch.batchId !== trackedBatchRef.current) {
        trackedBatchRef.current = batch.batchId;
        mergedUploadFileIdsRef.current = new Set();
      }
      setUploadBatch(batch);
      setUploading(batch.status === 'running');

      const newDocs = batch.files.filter(
        f => f.status === 'complete' && f.completedDocument && !mergedUploadFileIdsRef.current.has(f.id),
      );
      if (newDocs.length > 0) {
        newDocs.forEach(f => mergedUploadFileIdsRef.current.add(f.id));
        setDocuments(prev => {
          const byId = new Map(prev.map(d => [d.id, d]));
          newDocs.forEach(f => {
            if (f.completedDocument) byId.set(f.completedDocument.id, f.completedDocument);
          });
          return Array.from(byId.values()).sort(
            (a, b) => b.uploadedAt.getTime() - a.uploadedAt.getTime(),
          );
        });
      }

      if (batch.status !== 'running' && batch.finishedAt && !reconciledBatchIdsRef.current.has(batch.batchId)) {
        reconciledBatchIdsRef.current.add(batch.batchId);
        void getLeadDocuments(leadId).then(setDocuments).catch(err => {
          console.error('Error refreshing documents after upload batch:', err);
        });
      }
    });
  }, [leadId]);

  useEffect(() => {
    if (!leadId) return;
    const q = query(leadCommentsCollectionRef(leadId), orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(
      q,
      snapshot => {
        onCommentCountChange?.(snapshot.size);
        const files: Array<{ att: CommentAttachment; createdAt: Date }> = [];
        snapshot.docs.forEach(d => {
          const data = d.data();
          const attachments = (data.attachments as CommentAttachment[] | undefined) || [];
          const createdAt = data.createdAt?.toDate?.() || new Date();
          attachments.forEach(att => files.push({ att, createdAt }));
        });
        setCommentFiles(files);
      },
      err => firestoreSnapshotListenError('AgentDocumentsHub', `leads/${leadId}/comments`, err),
    );
    return () => unsub();
  }, [leadId, onCommentCountChange]);

  useEffect(() => {
    if (!leadId) return;
    return subscribeToAnnotationsForLead(leadId, all => {
      const byKey = new Map<string, PdfAnnotation[]>();
      all.forEach(ann => {
        for (const key of [ann.documentId, ann.documentUrl]) {
          if (!key) continue;
          const arr = byKey.get(key);
          if (arr) arr.push(ann);
          else byKey.set(key, [ann]);
        }
      });
      setAnnotationsByKey(byKey);
      onLeadPinCountChange?.(all.filter(a => !a.source || a.source === 'user').length);
    });
  }, [leadId, onLeadPinCountChange]);

  useEffect(() => {
    if (!leadId) return;
    return subscribeToTakeoffLinksForLead(leadId, links => {
      const counts = new Map<string, number>();
      links.forEach(link => {
        counts.set(link.documentId, (counts.get(link.documentId) ?? 0) + 1);
      });
      setTakeoffCountByDocId(counts);
    });
  }, [leadId]);

  const allDocs = useMemo((): DisplayDoc[] => {
    const seen = new Set<string>();
    const result: DisplayDoc[] = [];
    documents.forEach(d => {
      if (!seen.has(d.url)) {
        seen.add(d.url);
        result.push({
          ...d,
          id: d.id || d.url,
          name: d.name,
          url: d.url,
          size: d.size,
          mimeType: d.mimeType || 'application/pdf',
          dealId: d.dealId,
          source: 'upload' as const,
          uploadedAt: d.uploadedAt,
        });
      }
    });
    commentFiles.forEach(({ att, createdAt }) => {
      if (!seen.has(att.url)) {
        seen.add(att.url);
        result.push({
          id: att.url,
          name: att.name,
          url: att.url,
          size: att.size,
          mimeType: att.type || 'application/octet-stream',
          source: 'comment',
          uploadedAt: createdAt,
        });
      }
    });
    return result.sort((a, b) => (b.uploadedAt?.getTime() ?? 0) - (a.uploadedAt?.getTime() ?? 0));
  }, [documents, commentFiles]);

  useEffect(() => {
    onDocCountChange?.(allDocs.length);
  }, [allDocs.length, onDocCountChange]);

  // Scope to the active project: its own files plus untagged lead-level files
  // (untagged files belong to no project and would otherwise be unreachable).
  const scopedDocs = useMemo(() => {
    if (!activeDealId) return allDocs;
    return allDocs.filter(d => !d.dealId || d.dealId === activeDealId);
  }, [allDocs, activeDealId]);

  const activeProjectName = useMemo(
    () => (activeDealId ? deals?.find(d => d.id === activeDealId)?.name : undefined),
    [deals, activeDealId],
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !user?.id) return;
    const leadLabel = `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || 'Lead';
    leadDocumentUploadManager.enqueue(
      leadId,
      leadLabel,
      files,
      user.name || user.email,
      activeDealId || undefined,
    );
    e.target.value = '';
  };

  const handleDelete = async (doc: DisplayDoc) => {
    if (doc.source !== 'upload' || !doc.id || !('storagePath' in doc)) return;
    if (!window.confirm(`Delete "${doc.name}"?`)) return;
    setDeletingId(doc.id);
    try {
      await deleteLeadDocument(doc.id, doc.storagePath);
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
    } catch (err) {
      console.error('Error deleting document:', err);
      alert('Failed to delete document.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleAssignProject = async (doc: DisplayDoc, newDealId: string | null) => {
    if (doc.source !== 'upload' || !doc.id) return;
    try {
      await updateLeadDocumentDealId(doc.id, newDealId);
      setDocuments(prev => prev.map(d => d.id === doc.id ? { ...d, dealId: newDealId || undefined } : d));
    } catch (err) {
      console.error('Error assigning document to project:', err);
    }
  };

  const openDoc = useCallback((doc: DisplayDoc, opts?: { annotationId?: string; pageNumber?: number }) => {
    if (isImageType(doc.mimeType) && onOpenImage) {
      onOpenImage({ url: doc.url, name: doc.name, id: doc.id, mimeType: doc.mimeType }, { annotationId: opts?.annotationId });
      return;
    }
    if (isCsvType(doc.mimeType, doc.name) && onOpenCsv) {
      onOpenCsv({ url: doc.url, name: doc.name, id: doc.id, mimeType: doc.mimeType }, { annotationId: opts?.annotationId });
      return;
    }
    onOpenPdf({ url: doc.url, name: doc.name, id: doc.id }, opts);
  }, [onOpenPdf, onOpenImage, onOpenCsv]);

  const biddingAnalysis = lead.biddingAnalysis;
  const canAssign = !!(deals && deals.length > 0);

  return (
    <div className="adh-root" data-tour="cw-agent-documents">
      <div className="adh-header">
        <span className="adh-header-label">
          <FolderOpen size={14} /> Documents &amp; Plans
          {activeProjectName && <span className="adh-header-project" title={activeProjectName}>{activeProjectName}</span>}
        </span>
        <span className="adh-header-spacer" />
        <span className="adh-header-count">{scopedDocs.length} file{scopedDocs.length === 1 ? '' : 's'}</span>
        <button
          type="button"
          className="adh-upload-btn"
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? <Loader2 size={14} className="spin" /> : <Plus size={14} />}
          <span>{uploading ? 'Uploading…' : 'Upload'}</span>
        </button>
        <input ref={fileInputRef} type="file" multiple onChange={handleFileSelect} style={{ display: 'none' }} />
      </div>

      {uploadBatch && uploadBatch.status === 'running' && (
        <div className="adh-upload-panel" role="status" aria-live="polite">
          <div className="adh-upload-panel-head">
            <Loader2 size={14} className="spin" />
            <span className="adh-upload-panel-title">Uploading in background</span>
            <span className="adh-upload-panel-eta">{formatBatchEta(uploadBatch)}</span>
          </div>
          <div className="adh-upload-panel-summary">{formatBatchSummary(uploadBatch)}</div>
          <div className="adh-upload-panel-bar" role="progressbar" aria-valuenow={Math.round(getBatchProgressPct(uploadBatch))} aria-valuemin={0} aria-valuemax={100}>
            <div className="adh-upload-panel-bar-fill" style={{ width: `${getBatchProgressPct(uploadBatch)}%` }} />
          </div>
          <p className="adh-upload-panel-hint">
            You can leave this lead — progress stays in the upload panel at the bottom-right of the app.
          </p>
          <ul className="adh-upload-file-list">
            {uploadBatch.files.map(f => (
              <li key={f.id} className={`adh-upload-file-row adh-upload-file-row--${f.status}`}>
                <span className="adh-upload-file-name" title={f.fileName}>{f.fileName}</span>
                <span className="adh-upload-file-meta">
                  {formatUploadBytes(f.fileSize)}
                  {f.status === 'uploading' && f.fileSize > 0 && (
                    <> · {Math.round((f.bytesTransferred / f.fileSize) * 100)}%</>
                  )}
                  {f.status === 'complete' && ' · Done'}
                  {f.status === 'pending' && ' · Queued'}
                  {f.status === 'error' && ' · Failed'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {loading ? (
        <div className="adh-loading">
          <div className="law-loadbar" style={{ width: 160 }} />
          <span>Loading documents…</span>
        </div>
      ) : scopedDocs.length === 0 ? (
        <div className="adh-empty">
          <span className="adh-empty-icon"><FileText size={24} /></span>
          <span className="adh-empty-title">No documents for this project</span>
          <p className="adh-empty-hint">
            Upload drawings or plans. The bidding agent reads each file to write a synopsis,
            mark up timber, and prep a proposal.
          </p>
        </div>
      ) : (
        <ul className="adh-table pm-scroll">
          {scopedDocs.map(doc => (
            <AgentDocumentRow
              key={doc.id}
              doc={doc}
              deals={deals}
              canAssign={canAssign}
              deletingId={deletingId}
              annotations={annotationsForDocument(annotationsByKey, doc.id, doc.url)}
              takeoffCount={takeoffCountByDocId.get(doc.id) ?? 0}
              biddingAnalysisRefsThisDoc={
                biddingAnalysis?.timberExtraction?.items?.some(it => it.documentId && (it.documentId === doc.id || it.documentId === doc.url)) ?? false
              }
              onOpen={openDoc}
              onDelete={handleDelete}
              onAssign={handleAssignProject}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

// ─── Per-document row (condensed) ───

interface AgentDocumentRowProps {
  doc: DisplayDoc;
  deals?: DealFilterOption[];
  canAssign: boolean;
  deletingId: string | null;
  annotations: PdfAnnotation[];
  takeoffCount: number;
  biddingAnalysisRefsThisDoc: boolean;
  onOpen: (doc: DisplayDoc, opts?: { annotationId?: string; pageNumber?: number }) => void;
  onDelete: (doc: DisplayDoc) => void;
  onAssign: (doc: DisplayDoc, newDealId: string | null) => void;
}

const AgentDocumentRow: React.FC<AgentDocumentRowProps> = ({
  doc,
  deals,
  canAssign,
  deletingId,
  annotations,
  takeoffCount,
  biddingAnalysisRefsThisDoc,
  onOpen,
  onDelete,
  onAssign,
}) => {
  const [expanded, setExpanded] = useState(false);

  const agentPins = annotations.filter(a => a.source === 'biddingAgent' || a.source === 'marketingAgent');
  const userPins = annotations.filter(a => !a.source || a.source === 'user');

  // Agent markups ordered by page for the "Marked up" hover panel.
  const agentMarkups = [...agentPins].sort(
    (a, b) => (a.pageNumber ?? 0) - (b.pageNumber ?? 0) || a.createdAt.getTime() - b.createdAt.getTime(),
  );

  const typeLabel = docTypeLabel(doc.mimeType, doc.name);
  const synopsis = ('agentSynopsis' in doc ? doc.agentSynopsis : undefined) || undefined;
  const untagged = !doc.dealId;

  const handleDownload = async () => {
    try {
      const resp = await fetch(doc.url);
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = doc.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(doc.url, '_blank');
    }
  };

  return (
    <li className="adh-row-wrap">
      <div className="adh-row">
        <button
          type="button"
          className="adh-row-main"
          onClick={() => onOpen(doc)}
          title={`Open ${doc.name}`}
        >
          <span className="adh-row-icon" data-type={typeLabel}>{typeIcon(typeLabel)}</span>
          <span className="adh-row-name">{doc.name}</span>
        </button>

        <span className="adh-row-chips">
          {untagged && canAssign && <span className="adh-mini-chip" data-kind="untagged">Lead file</span>}
          {doc.source === 'comment' && <span className="adh-mini-chip" data-kind="comment">Comment</span>}
          {biddingAnalysisRefsThisDoc && <span className="adh-mini-chip" data-kind="analyzed">Analyzed</span>}
          {takeoffCount > 0 && <span className="adh-mini-chip" data-kind="takeoff">Take-off</span>}
          {agentPins.length > 0 && (
            <span className="adh-markup">
              <span
                className="adh-mini-chip adh-chip-interactive"
                data-kind="markedup"
                tabIndex={0}
                role="button"
                aria-haspopup="menu"
                aria-label={`${agentPins.length} agent markup${agentPins.length === 1 ? '' : 's'} — hover or focus to view`}
              >
                <MapPin size={9} /> {agentPins.length}
              </span>
              <div className="adh-markup-panel" role="menu">
                <div className="adh-markup-panel-head">Agent markups</div>
                {agentMarkups.map(pin => (
                  <button
                    key={pin.id}
                    type="button"
                    className="adh-markup-row"
                    role="menuitem"
                    onClick={() => onOpen(doc, { annotationId: pin.id, pageNumber: pin.pageNumber })}
                    title="Open this markup on the plan"
                  >
                    <span className="adh-markup-page">{pin.pageNumber != null ? `p.${pin.pageNumber}` : '—'}</span>
                    <span className="adh-markup-text">{pin.content || 'Markup'}</span>
                  </button>
                ))}
              </div>
            </span>
          )}
          {userPins.length > 0 && (
            <span className="adh-mini-stat" title="Team comment pins"><MessageSquare size={10} /> {userPins.length}</span>
          )}
        </span>

        <span className="adh-row-meta">
          {doc.uploadedAt && <span>{new Date(doc.uploadedAt).toLocaleDateString()}</span>}
          <span>{formatSize(doc.size)}</span>
        </span>

        <span className="adh-row-actions">
          {canAssign && doc.source === 'upload' && (
            <select
              className="adh-assign-select"
              value={doc.dealId || ''}
              onClick={e => e.stopPropagation()}
              onChange={e => onAssign(doc, e.target.value || null)}
              title="Assign to project"
            >
              <option value="">No project</option>
              {deals!.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
          <button
            type="button"
            className="adh-icon-btn"
            title="Download"
            aria-label="Download document"
            onClick={handleDownload}
          >
            <Download size={13} />
          </button>
          {doc.source === 'upload' && (
            <button
              type="button"
              className="adh-icon-btn adh-icon-danger"
              title="Delete"
              aria-label="Delete document"
              disabled={deletingId === doc.id}
              onClick={() => onDelete(doc)}
            >
              {deletingId === doc.id ? <Loader2 size={13} className="spin" /> : <Trash2 size={13} />}
            </button>
          )}
          {synopsis && (
            <button
              type="button"
              className={`adh-icon-btn adh-expand-btn ${expanded ? 'open' : ''}`}
              title={expanded ? 'Hide agent synopsis' : 'Show agent synopsis'}
              aria-expanded={expanded}
              onClick={() => setExpanded(o => !o)}
            >
              <ChevronDown size={13} />
            </button>
          )}
        </span>
      </div>

      {expanded && synopsis && (
        <div className="adh-row-synopsis">
          <span className="adh-synopsis-label"><Sparkles size={11} /> Agent synopsis</span>
          {synopsis.text}
        </div>
      )}
    </li>
  );
};

export default AgentDocumentsHub;
